import { HocuspocusProvider, HocuspocusProviderWebsocket } from '@hocuspocus/provider';
import { Awareness } from 'y-protocols/awareness';
import * as Y from 'yjs';
import type { DocPersistence, NetworkStatus } from '@lumen/platform';

export interface DocHandle {
  name: string;
  doc: Y.Doc;
  awareness: Awareness;
  /** Resolves when local (IndexedDB) state is loaded. */
  whenLoaded: Promise<void>;
  /** Resolves after the first server sync, or never while offline. */
  whenSynced: Promise<void>;
  /** The connection was refused or degraded to read-only for this document. */
  readonly readOnly: boolean;
  release(): void;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

interface Entry {
  handle: DocHandle;
  refs: number;
  provider: HocuspocusProvider | null;
  local: { destroy(): void };
}

/**
 * Opens Y.Docs by name, keeps them persisted locally and (when a sync URL is configured)
 * connected to the server through a single multiplexed WebSocket.
 */
export class DocManager {
  private entries = new Map<string, Entry>();
  private socket: HocuspocusProviderWebsocket | null = null;
  private statusListeners = new Set<(s: ConnectionStatus) => void>();
  private _status: ConnectionStatus = 'disconnected';
  private readOnlyDocs = new Set<string>();
  /** Documents the server refused; retried when the user's access may have changed. */
  private refused = new Set<string>();
  private retryTimers = new Map<string, { timer: ReturnType<typeof setTimeout>; delay: number }>();
  private readOnlyListeners = new Set<() => void>();
  private stopNetwork: (() => void) | undefined;

  constructor(
    private readonly opts: {
      persistence: DocPersistence;
      syncUrl: string | null;
      token: () => string | null;
      user: { id: string; name: string; color: string };
      /** When the device reports it is offline, the connection is closed at once and opened again when it is back. */
      network?: NetworkStatus;
    },
  ) {
    if (opts.syncUrl) {
      const socket = new HocuspocusProviderWebsocket({ url: opts.syncUrl, autoConnect: true, delay: 300, minDelay: 100, maxDelay: 3000, factor: 1.5 });
      this.socket = socket;
      this._status = 'connecting';
      socket.on('status', ({ status }: { status: string }) => this.setStatus(status as ConnectionStatus));
      // A socket that was open when the network went away can stay "open" for a long time (Safari, Firefox), and
      // edits would wait on it. Treat the device's offline signal as authoritative.
      this.stopNetwork = opts.network?.subscribe((online) => {
        if (online) void socket.connect();
        else socket.disconnect();
      });
    }
  }

  get status(): ConnectionStatus {
    return this._status;
  }

  private setStatus(s: ConnectionStatus) {
    this._status = s;
    for (const l of [...this.statusListeners]) l(s);
  }

  onStatus(listener: (s: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  isReadOnly(name: string): boolean {
    return this.readOnlyDocs.has(name);
  }

  onReadOnlyChange(listener: () => void): () => void {
    this.readOnlyListeners.add(listener);
    return () => this.readOnlyListeners.delete(listener);
  }

  open(name: string): DocHandle {
    const existing = this.entries.get(name);
    if (existing) {
      existing.refs++;
      return existing.handle;
    }
    const doc = new Y.Doc();
    const local = this.opts.persistence.attach(name, doc);
    let provider: HocuspocusProvider | null = null;
    let awareness: Awareness;
    let resolveSynced!: () => void;
    const whenSynced = new Promise<void>((r) => (resolveSynced = r));
    if (this.socket) {
      provider = new HocuspocusProvider({
        websocketProvider: this.socket,
        name,
        document: doc,
        token: () => this.opts.token() ?? '',
        onSynced: () => resolveSynced(),
        onAuthenticated: ({ scope }: { scope: string }) => {
          this.refused.delete(name);
          clearTimeout(this.retryTimers.get(name)?.timer);
          this.retryTimers.delete(name);
          if (scope === 'read-only') this.readOnlyDocs.add(name);
          else this.readOnlyDocs.delete(name);
          for (const l of [...this.readOnlyListeners]) l();
        },
        // The server announces permission changes on open connections (ADR 0006).
        onStateless: ({ payload }: { payload: string }) => {
          try {
            const msg = JSON.parse(payload) as { readOnly?: boolean };
            if (typeof msg.readOnly !== 'boolean') return;
            if (msg.readOnly) this.readOnlyDocs.add(name);
            else this.readOnlyDocs.delete(name);
            for (const l of [...this.readOnlyListeners]) l();
          } catch {
            /* not a permission notice */
          }
        },
        onAuthenticationFailed: () => {
          this.refused.add(name);
          // Access can arrive later (a share racing with this attempt): ask again with backoff.
          const delay = Math.min((this.retryTimers.get(name)?.delay ?? 500) * 2, 15_000);
          clearTimeout(this.retryTimers.get(name)?.timer);
          this.retryTimers.set(name, { delay, timer: setTimeout(() => this.rehandshake(name), delay) });
          this.readOnlyDocs.add(name);
          for (const l of [...this.readOnlyListeners]) l();
        },
      });
      provider.attach();
      awareness = provider.awareness!;
    } else {
      awareness = new Awareness(doc);
    }
    awareness.setLocalStateField('user', this.opts.user);
    const readOnlyDocs = this.readOnlyDocs;
    const handle: DocHandle = {
      name,
      doc,
      awareness,
      whenLoaded: local.whenLoaded,
      whenSynced,
      get readOnly() {
        return readOnlyDocs.has(name);
      },
      release: () => this.release(name),
    };
    this.entries.set(name, { handle, refs: 1, provider, local });
    return handle;
  }

  private release(name: string) {
    const e = this.entries.get(name);
    if (!e) return;
    e.refs--;
    if (e.refs > 0) return;
    // Keep docs warm for a moment so quick navigation back does not reconnect.
    setTimeout(() => {
      const cur = this.entries.get(name);
      if (!cur || cur.refs > 0) return;
      cur.provider?.destroy();
      cur.local.destroy();
      cur.handle.doc.destroy();
      this.entries.delete(name);
      clearTimeout(this.retryTimers.get(name)?.timer);
      this.retryTimers.delete(name);
      this.refused.delete(name);
    }, 30_000);
  }

  /** Asks the server again for documents it refused (after a share or permission change). */
  retryRefused(): void {
    for (const name of [...this.refused]) this.rehandshake(name);
  }

  /** Authenticates again and restarts the sync handshake (the first one was dropped with the refusal). */
  private rehandshake(name: string): void {
    const provider = this.entries.get(name)?.provider;
    if (!provider) return;
    void provider.sendToken().then(() => provider.startSync());
  }

  /** Whether any open document has local changes the server has not acknowledged. */
  get hasUnsyncedChanges(): boolean {
    return [...this.entries.values()].some((e) => e.provider?.hasUnsyncedChanges);
  }

  destroy(): void {
    for (const t of this.retryTimers.values()) clearTimeout(t.timer);
    this.retryTimers.clear();
    for (const e of this.entries.values()) {
      e.provider?.destroy();
      e.local.destroy();
      e.handle.doc.destroy();
    }
    this.entries.clear();
    this.stopNetwork?.();
    this.socket?.destroy();
  }
}
